<script setup lang="ts">
import Button from 'primevue/button'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import { ref } from 'vue'

const authStore = useAuthStore()

// Form state
const isLogin = ref(true)

// Form data
const name = ref('')
const email = ref('')
const password = ref('')

// Toggle between login and register with smooth transition
function toggleMode() {
  isLogin.value = !isLogin.value
  // Reset form when switching
  name.value = ''
  email.value = ''
  password.value = ''
}

// Handle form submission
function handleSubmit() {
  if (isLogin.value) {
    authStore.login({
      email: email.value,
      password: password.value,
    })
  }
  else {
    authStore.register({
      email: email.value,
      password: password.value,
      name: name.value,
    })
  }
}
</script>

<template>
  <div class="flex items-center justify-center p-4 relative overflow-hidden">
    <!-- Main form container -->
    <div class="w-full max-w-sm rounded-2xl p-8 shadow border border-gray-800 backdrop-blur-sm">
      <!-- Header with smooth transition -->
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold  transition-all duration-500 ease-in-out">
          {{ isLogin ? 'Welcome Back' : 'Create Account' }}
        </h1>
        <p class="text-gray-700 mt-2 transition-all duration-500 ease-in-out">
          {{ isLogin ? 'Sign in to your account' : 'Join us today' }}
        </p>
      </div>

      <!-- Form with smooth transitions -->
      <form class="space-y-6" @submit.prevent="handleSubmit">
        <!-- Name field - only show for register -->
        <div
          class="transition-all duration-500 ease-in-out overflow-hidden"
          :class="isLogin ? 'max-h-0 opacity-0 -mb-6' : 'max-h-20 opacity-100'"
        >
          <div v-show="!isLogin">
            <IconField>
              <InputIcon>
                <i class="fa-light fa-circle-user" />
              </InputIcon>
              <InputText
                id="name"
                v-model="name"
                type="text"
                placeholder="Full Name"
                fluid
              />
            </IconField>
          </div>
        </div>

        <!-- Email field -->
        <IconField>
          <InputIcon>
            <i class="fa-light fa-envelope" />
          </InputIcon>
          <InputText
            id="email"
            v-model="email"
            type="email"
            placeholder="Email Address"
            fluid
            autofocus
          />
        </IconField>

        <!-- Password field -->
        <IconField>
          <InputIcon>
            <i class="fa-light fa-lock" />
          </InputIcon>
          <Password
            id="password"
            v-model="password"
            placeholder="Password"
            fluid
            :feedback="false"
            toggle-mask
          />
        </IconField>

        <!-- Submit button -->
        <Button
          type="submit"
          :label="isLogin ? 'Sign In' : 'Get Started'"
          class="w-full   text-black font-semibold rounded-xl transition-all duration-300 transform "
        />

        <!-- Toggle mode section -->
        <div class="text-center pt-6 border-t border-gray-800">
          <p class="text-gray-700 text-sm mb-3">
            {{ isLogin ? "Don't have an account?" : "Already have an account?" }}
          </p>
          <div class="flex justify-center mt-2">
            <button
              type="button"
              class="px-6 py-2 font-medium text-sm transition-all duration-300 rounded-lg"
              @click="toggleMode"
            >
              {{ isLogin ? 'Sign up' : 'Sign in' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
